// mixer.ts ↔ game/mixer.cpp — the table-driven ADPCM channel mixer.
//
// Sounds are 4-bit ADPCM: two nibbles per byte (high then low), each indexing a
// per-step DELTA table (byte arithmetic, wraps mod 256) and a NEXT-STEP map
// (89 steps). Output is 8-bit unsigned PCM, two samples per input byte; active
// channels mix by integer-averaging their samples, silence is 128. The two
// 89x16 tables are extracted from mixer.cpp programmatically
// (client/tools/gen-adpcm-tables.mjs -> adpcm-tables.json).

import tables from './adpcm-tables.json' with { type: 'json' };

const gabAdpcmSteppings: number[][] = tables.steppings;
const ganStepIndexMap: number[][] = tables.stepIndexMap;

/** mixer.h:8 — one playing sound: a cursor over its ADPCM bytes + decoder state. */
export interface Channel {
  pb: Uint8Array; // the sound's compressed bytes
  ib: number; // cursor (C++ pb pointer)
  ibEnd: number; // end (C++ pbEnd)
  bSampleLast: number; // byte accumulator (wraps mod 256)
  nStepIndex: number;
}

export function newChannel(pb: Uint8Array): Channel {
  // soundmgr starts channels at sample 128, step 0 (silence baseline)
  return { pb, ib: 0, ibEnd: pb.length, bSampleLast: 128, nStepIndex: 0 };
}

// game/mixer.cpp:197-225 — decode + mix cchnl channels into cb bytes of 8-bit
// unsigned PCM. Each loop iteration consumes ONE compressed byte per active
// channel and emits TWO output samples (the high nibble then the low nibble).
export function MixChannels(achnl: Channel[], pb: Uint8Array, cb: number): void {
  let pbT = 0;
  while (pbT < cb) {
    let cMixed = 0;
    let cSum1 = 0;
    let cSum2 = 0;

    for (const pchnl of achnl) {
      if (pchnl.ib < pchnl.ibEnd) {
        let nDelta = (pchnl.pb[pchnl.ib] >> 4) & 0xf;
        pchnl.bSampleLast =
          (pchnl.bSampleLast + gabAdpcmSteppings[pchnl.nStepIndex][nDelta]) & 0xff;
        cSum1 += pchnl.bSampleLast;
        pchnl.nStepIndex = ganStepIndexMap[pchnl.nStepIndex][nDelta];
        nDelta = pchnl.pb[pchnl.ib] & 0xf;
        pchnl.bSampleLast =
          (pchnl.bSampleLast + gabAdpcmSteppings[pchnl.nStepIndex][nDelta]) & 0xff;
        cSum2 += pchnl.bSampleLast;
        pchnl.nStepIndex = ganStepIndexMap[pchnl.nStepIndex][nDelta];
        pchnl.ib++;
        cMixed++;
      }
    }
    if (cMixed === 0) {
      pb[pbT++] = 128;
      pb[pbT++] = 128;
    } else {
      pb[pbT++] = Math.floor(cSum1 / cMixed);
      pb[pbT++] = Math.floor(cSum2 / cMixed);
    }
  }
}

/** Decode one whole sound to PCM (a single-channel MixChannels run). */
export function decodeAdpcm(compressed: Uint8Array): Uint8Array {
  const out = new Uint8Array(compressed.length * 2);
  MixChannels([newChannel(compressed)], out, out.length);
  return out;
}
